import { forwardRef, useState } from "react";
import { Loader2, PenLine, Plus, RefreshCw } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { MailStats } from "../../../lib/mail";
import { labelColor, type CrmLabel } from "../../../lib/crmLabels";
import { Btn, IconBtn } from "../../ui";
import { FavoritesSection, MailboxTree, useMailboxDrag, type MailRail, type MailView } from "./rail";

const FOLDS_KEY = "crm.mail.rail.folds";

/** Which sections are folded, per person and browser. A convenience: losing it opens everything. */
function readFolds(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(FOLDS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/**
 * The inbox's own left rail: Favorites, then one folder tree per mailbox (the
 * shared one, then the reader's own), then the operator's labels and the
 * campaigns that put mail in the shared inbox.
 *
 * Each tree has the same six rows - Inbox, Drafts, Scheduled, Sent, Archive,
 * Spam - and the account's other server folders folded under "More folders".
 * "Unread", "Matched to a company" and "Campaign replies" used to be rows
 * here; they are cuts of an inbox, not places mail lives, and they moved to
 * the list's filter menu, where they narrow whichever inbox is open.
 *
 * A label narrows the open inbox rather than replacing it, which is why picking
 * one leaves the row highlighted: "unread, complaints" is a question an
 * operator actually asks.
 */
export const FolderRail = forwardRef<
  HTMLDivElement,
  {
    view: MailView;
    onView: (view: MailView) => void;
    rail: MailRail;
    campaignId: string | null;
    onCampaign: (id: string | null) => void;
    campaigns: { id: string; name: string }[];
    labels: CrmLabel[];
    tag: string | null;
    onTag: (id: string | null) => void;
    stats: MailStats | null;
    onCompose: () => void;
    onSync: () => void;
    syncing: boolean;
    /** The address the sync button names: the open mailbox, or all of them. */
    syncLabel: string;
    onToast: (msg: string) => void;
    onAddMailbox: () => void;
    onEditMailbox: (id: string) => void;
    /** Only read on a phone, where this rail is a sheet over the panes. */
    open: boolean;
    /** Something in the rail was chosen. On a phone that closes the sheet: the
     *  answer to "show me the unread ones" is the list, not the rail. */
    onPicked: () => void;
  }
>(function FolderRail(
  {
    view,
    onView,
    rail,
    campaignId,
    onCampaign,
    campaigns,
    labels,
    tag,
    onTag,
    stats,
    onCompose,
    onSync,
    syncing,
    syncLabel,
    onToast,
    onAddMailbox,
    onEditMailbox,
    open,
    onPicked,
  },
  // The workspace's rail divider measures this column to know where to sit and
  // how far it may be dragged.
  ref,
) {
  const t = useT();
  const [folds, setFolds] = useState<Record<string, boolean>>(readFolds);
  const isOpen = (id: string) => folds[id] !== false;
  const toggle = (id: string) => {
    const next = { ...folds, [id]: !isOpen(id) };
    setFolds(next);
    try {
      localStorage.setItem(FOLDS_KEY, JSON.stringify(next));
    } catch {
      // Private window or blocked storage: the folds just do not survive a reload.
    }
  };
  const pick = (next: MailView) => {
    onView(next);
    onPicked();
  };
  const own = rail.mailboxes.filter((m) => m.kind === "own").length;
  const dragOf = useMailboxDrag(
    rail.mailboxes.map((m) => m.id),
    rail.moveMailbox,
  );
  const threads = view.kind === "threads";

  return (
    <div ref={ref} className={cn("crm-card crm-work-col crm-pane-side", open && "is-open")}>
      {/* Brand, not dark. The selected folder right below it is a black pill,
          and an action drawn in the same black reads as a second selection -
          two identical shapes, one of which is not a place you can be.

          The plate around it is what stays put while the folders scroll (see
          `.crm-pane-act`); the button itself is unchanged. */}
      <div className="crm-pane-act">
        <Btn tone="brand" onClick={onCompose}>
          <PenLine className="size-4" />
          {t("mail.compose")}
        </Btn>
      </div>

      <FavoritesSection rail={rail} view={view} onView={pick} open={isOpen("fav")} onToggle={() => toggle("fav")} />

      {rail.mailboxes.map((box) => (
        <MailboxTree
          key={box.id}
          box={box}
          view={view}
          onView={pick}
          rail={rail}
          open={isOpen(box.id)}
          onToggle={() => toggle(box.id)}
          onSettings={box.kind === "own" ? () => onEditMailbox(box.id) : undefined}
          onToast={onToast}
          drag={rail.mailboxes.length > 1 ? dragOf(box.id) : undefined}
        />
      ))}

      {own < rail.max ? (
        <button type="button" className="crm-mtag" onClick={onAddMailbox}>
          <Plus className="size-3.5 shrink-0" />
          <span className="crm-mtag-l">{t("mail.box.add")}</span>
        </button>
      ) : null}

      {labels.length > 0 ? (
        <>
          <div className="crm-msect">{t("mail.labels")}</div>
          {labels.map((label) => (
            <button
              key={label.id}
              type="button"
              className={cn("crm-mtag", tag === label.id && threads && "is-on")}
              aria-pressed={tag === label.id && threads}
              onClick={() => {
                // A label narrows a conversation list, so picking one away
                // from one goes back to the shared inbox first.
                if (!threads) onView({ kind: "threads", mailbox: "shared", status: "open" });
                onTag(tag === label.id ? null : label.id);
                onPicked();
              }}
            >
              <i style={{ background: labelColor(label.tone) }} />
              <span className="crm-mtag-l">{label.name}</span>
            </button>
          ))}
        </>
      ) : null}

      {campaigns.length > 0 && (stats?.campaignThreads ?? 0) > 0 ? (
        <>
          <div className="crm-msect">{t("mail.campaigns")}</div>
          {campaigns.slice(0, 8).map((c, i) => (
            // `is-on`, not the hand-rolled Tailwind pair it used to carry: that
            // matched the background and the colour but missed the 650 weight,
            // so a selected campaign read lighter than a selected label eight
            // rows above it.
            <button
              key={c.id}
              type="button"
              className={cn("crm-mtag", campaignId === c.id && threads && "is-on")}
              aria-pressed={campaignId === c.id && threads}
              onClick={() => {
                // Campaigns write from the shared mailbox, so their replies
                // are in its inbox.
                onCampaign(campaignId === c.id ? null : c.id);
                onView({ kind: "threads", mailbox: "shared", status: "open" });
                onPicked();
              }}
            >
              <i
                style={{
                  background: [
                    "var(--color-primary)",
                    "var(--color-primary-2)",
                    "var(--color-success)",
                    "var(--color-warning)",
                  ][i % 4],
                }}
              />
              <span className="crm-mtag-l">{c.name}</span>
            </button>
          ))}
        </>
      ) : null}

      {/* Pulling the mailbox is a rail action, not a page action: it belongs
          with the folders it refills, not next to Compose. Sticky, because the
          trees, the labels and the campaigns together overflow the rail and
          the pull button was scrolling out of reach. */}
      <div className="crm-pane-foot">
        <IconBtn onClick={onSync} disabled={syncing} title={t("mail.box.sync")} aria-label={t("mail.box.sync")}>
          {syncing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </IconBtn>
        <span className="crm-trunc text-[11px] text-muted-foreground">{syncLabel}</span>
      </div>
    </div>
  );
});
